<template>
  <div
    class="training-section-program-item"
    :class="{ 'is-open': isOpen }"
  >
    <button
      type="button"
      class="program-header"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggle"
    >
      <h3
        class="program-name"
        v-html="markdownToHTML(name || '')"
      ></h3>
      <span class="program-arrow">
        <Icon name="next" />
      </span>
    </button>
    <transition
      :css="false"
      @enter="enter"
      @leave="leave"
    >
      <div
        v-if="isOpen"
        class="program-body"
      >
        <div class="program-body-inner">
          <div class="icon-blocks-grid">
            <TrainingSectionIconBlockItem
              v-for="(block, index) in iconBlocks"
              :key="`icon-block-${index}`"
              :icon="block.Icon"
              :title="block.Title"
              :text="block.Text"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import TrainingSectionIconBlockItem from './TrainingSectionIconBlockItem.vue';

// Markdown — для жирных фрагментов («…») в названии программы
const md = require('markdown-it')({
  html: true,
  linkify: true,
  typographer: true,
});

export default {
  name: 'TrainingSectionProgramItem',
  components: {
    Icon,
    TrainingSectionIconBlockItem,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    iconBlocks: {
      type: Array,
      default: () => [],
    },
    openByDefault: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: this.openByDefault,
    };
  },
  methods: {
    markdownToHTML(content) {
      return md.renderInline(content);
    },
    toggle() {
      this.isOpen = !this.isOpen;
    },
    enter(el, done) {
      const anim = el.animate(
        [{ height: '0px' }, { height: `${el.scrollHeight}px` }],
        { duration: 350, easing: 'ease-in-out' },
      );
      anim.onfinish = done;
    },
    leave(el, done) {
      const anim = el.animate(
        [{ height: `${el.scrollHeight}px` }, { height: '0px' }],
        { duration: 350, easing: 'ease-in-out' },
      );
      anim.onfinish = done;
    },
  },
};
</script>

<style lang="scss" scoped>
.training-section-program-item {
  width: 100%;

  &:not(:last-child) {
    // Синий разделитель между карточками: по макету 3px на десктопе,
    // 2px на мобайле (фиксированные px, не масштабируем дробно).
    border-bottom: 3px solid color(main);

    @include rule(
      border-bottom-width,
      (
        xl: 3px,
        lg: 3px,
        md: 2px,
        sm: 2px,
      )
    );
  }

  .program-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    cursor: pointer;
    color: color(basic);

    @include wRule(
      padding,
      (
        xl: 50 0,
        lg: 42 0,
        md: 37 0,
        sm: 32 0,
      )
    );

    @include wRule(
      grid-column-gap,
      (
        xl: 30,
        lg: 24,
        md: 20,
        sm: 24,
      )
    );
  }

  .program-name {
    flex: 1 1 auto;
    margin: 0;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.01em;

    @include wRule(
      font-size,
      (
        xl: 30,
        lg: 26,
        md: 24,
        sm: 21,
      )
    );

    @include wRule(
      padding-right,
      (
        xl: 30,
        lg: 24,
        md: 20,
        sm: 24,
      )
    );

    ::v-deep(strong),
    ::v-deep(b) {
      font-weight: 700;
    }
  }

  .program-arrow {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: color(gray-shevron); // серый шеврон по макету (#838589)
    transform: rotate(90deg);
    transform-origin: 50% 50%;
    transition: transform 0.35s ease-in-out;

    // Шеврон повёрнут на 90°, поэтому его визуальная ширина = высоте иконки.
    // Резервируем эту ширину в раскладке, иначе длинный заголовок выдавливает
    // шеврон из блока (раскладка иначе считает только узкую до-поворотную ширину).
    @include wRule(
      width,
      (
        xl: 40,
        lg: 40,
        md: 40,
        sm: 41,
      )
    );

    .icon {
      display: block;

      @include wRule(
        height,
        (
          xl: 40,
          lg: 40,
          md: 40,
          sm: 41,
        )
      );
    }
  }

  &.is-open {
    .program-arrow {
      transform: rotate(-90deg);
    }
  }

  .program-body {
    overflow: hidden;
  }

  .program-body-inner {
    @include wRule(
      padding,
      (
        xl: 58 0 116,
        lg: 49 0 94,
        md: 43 0 80,
        sm: 37 0 65,
      )
    );
  }

  .icon-blocks-grid {
    display: grid;

    grid-template-columns: 1.3fr 1fr 1fr 1fr;

    @include screen("md") {
      grid-template-columns: 1fr 1fr;
    }

    @include screen("sm", max) {
      grid-template-columns: 1fr 1fr;
    }

    @include wRule(grid-column-gap, (
      xl: 15,
      lg: 12,
      md: 10,
      sm: 30,
    ));

    @include wRule(grid-row-gap, (
      xl: 100,
      lg: 85,
      md: 76,
      sm: 65,
    ));
  }
}
</style>
